import {
  Suspense, lazy, useEffect, useMemo, useState,
} from 'react';
import {
  BrowserRouter, Navigate, Route, Routes,
} from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

import { FinderEvent, trackFinderEvent } from './api/analyticsEvents';
import Footer from './components/layout/Footer';
import Header from './components/layout/Header';
import LoadingScreen from './components/layout/LoadingScreen';
import PreviewBanner from './components/layout/PreviewBanner';
import SkipToMain from './components/layout/SkipToMain';
import { AppConfigProvider, useAppConfig } from './hooks/AppConfigContext';
import { HeaderActionsProvider } from './hooks/HeaderActionsContext';
import { useColorMode } from './hooks/useColorMode';
import { useDocumentLang } from './hooks/useDocumentLang';
import {
  detectUnsupportedBrowserLanguage,
  hasBrowserLanguageNotSupportedBeenTracked,
  markBrowserLanguageNotSupportedTracked,
} from './lib/browserLanguage';
import { env } from './lib/env';
import { initI18n } from './lib/i18n';
import { getSearchSessionId } from './lib/searchSession';
import { buildTheme } from './theme/buildTheme';

// Served from public/ at the SPA root.
const DEFAULT_FAVICON_URL = `${env.publicUrl}/favicon.ico`;

// Route-level code-split; Maps SDK only loads on Results / SchoolProfile.
const Landing = lazy(() => import('./pages/Landing'));
const Results = lazy(() => import('./pages/Results'));
const SchoolProfile = lazy(() => import('./pages/SchoolProfile'));
const MyList = lazy(() => import('./pages/MyList'));
const NotFound = lazy(() => import('./pages/NotFound'));

const ThemedShell = () => {
  const {
    config, loading, error, notFound,
  } = useAppConfig();
  const [mode, toggleMode] = useColorMode();
  const { i18n } = useTranslation();
  const theme = useMemo(() => buildTheme(config, mode), [config, mode]);
  // Gate on i18n resources too, so we never render raw `finder.*` keys.
  const [i18nReady, setI18nReady] = useState(false);
  useDocumentLang();

  useEffect(() => {
    if (loading) return undefined;
    let cancelled = false;
    setI18nReady(false);
    // Local bundle is the floor: ready even if BE overrides fail.
    initI18n(config.languagesEnabled)
      .catch(() => {})
      .then(() => {
        if (!cancelled) setI18nReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [loading, config.languagesEnabled]);

  // Track once when the browser language is unsupported and we fall back to English.
  useEffect(() => {
    if (loading || notFound || error || !config.isPublished) return;
    if (hasBrowserLanguageNotSupportedBeenTracked()) return;
    const unsupported = detectUnsupportedBrowserLanguage(config.languagesEnabled);
    if (!unsupported) return;
    markBrowserLanguageNotSupportedTracked();
    trackFinderEvent(FinderEvent.BrowserLanguageNotSupported, {
      language: i18n.language,
      search_session_id: getSearchSessionId(),
      browser_language: unsupported.browserLanguage,
      browser_base_language: unsupported.browserBaseLanguage,
      org_enabled_languages: config.languagesEnabled,
    });
  }, [loading, notFound, error, config.isPublished, config.languagesEnabled, i18n]);

  if (loading || !i18nReady) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoadingScreen />
      </ThemeProvider>
    );
  }

  // 404/error → standalone NotFound. A 200 unpublished response is an
  // authorized admin preview → full app + banner + noindex.
  if (notFound || error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  const isPreview = !config.isPublished;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Helmet>
        {config.orgName && <title>{config.orgName}</title>}
        <link rel="icon" href={config.faviconUrl || DEFAULT_FAVICON_URL} />
        <link rel="apple-touch-icon" href={config.faviconUrl || DEFAULT_FAVICON_URL} />
        <meta name="robots" content={isPreview ? 'noindex,nofollow' : 'index,follow'} />
      </Helmet>
      <BrowserRouter>
        <HeaderActionsProvider>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
            {isPreview && <PreviewBanner />}
            <SkipToMain />
            <Header mode={mode} onToggleMode={toggleMode} />
            <Suspense fallback={<LoadingScreen variant="inline" />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/results" element={<Results />} />
                <Route path="/schools/:slug" element={<SchoolProfile />} />
                <Route path="/my-list" element={<MyList />} />
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </Suspense>
            <Footer />
          </Box>
        </HeaderActionsProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

const App = () => (
  <HelmetProvider>
    <AppConfigProvider>
      <ThemedShell />
    </AppConfigProvider>
  </HelmetProvider>
);

export default App;
